import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminAppearanceProvider } from '../contexts/AdminAppearanceContext';
import EndShiftModal from './shift/EndShiftModal';
import CheckpointModal from './checkpoints/CheckpointModal';
import PartialSpoolModal from './checkpoints/PartialSpoolModal';
import LineStoppageAlertModal from './checkpoints/LineStoppageAlertModal';
import LanguageSelector from './LanguageSelector';
import '../styles/global.css';

// Zakładka "Zapotrzebowanie na barwnik" pokazuje się tam, gdzie build dostał
// REACT_APP_SHOW_DYE_DEMAND=true (docker-compose.yml, compose.develop.yaml i
// compose.production.yaml). CRA wstrzykuje tę zmienną na etapie builda, więc po zmianie trzeba
// przebudować obraz frontendu.
const showDyeDemand = process.env.REACT_APP_SHOW_DYE_DEMAND === 'true';

interface NavItem {
  tab: string;
  to: string | ((isPackaging: boolean) => string);
  labelKey: string;
  /** Build-time feature flag guard (e.g. dye demand). */
  enabled?: boolean;
}

interface NavGroup {
  key: string;
  labelKey: string;
  icon: string;
  items: NavItem[];
}

// Desktop menu is grouped into dropdowns (the flat list outgrew the bar); the mobile
// hamburger renders the same groups as headed sections. Every item is gated by can(tab).
const NAV_GROUPS: NavGroup[] = [
  {
    key: 'production', labelKey: 'nav.groupProduction', icon: '🏭',
    items: [
      { tab: 'production_lines', to: '/production-lines', labelKey: 'nav.productionLines' },
      { tab: 'large_warehouse', to: '/magazyn-duzych-gabarytow', labelKey: 'largeWarehouse.title' },
      { tab: 'packaging_station', to: (isPackaging) => (isPackaging ? '/stacja-pakowania' : '/admin/stacja-pakowania'), labelKey: 'nav.packagingStation' },
      { tab: 'dye_demand', to: '/zapotrzebowanie-barwnik', labelKey: 'nav.dyeDemand', enabled: showDyeDemand },
      { tab: 'surplus', to: '/nadwyzka-materialowa', labelKey: 'nav.surplus' },
    ],
  },
  {
    key: 'planning', labelKey: 'nav.groupPlanning', icon: '🗓️',
    items: [
      { tab: 'manage_assignments', to: '/manage-assignments', labelKey: 'nav.manageAssignments' },
      { tab: 'import', to: '/import', labelKey: 'nav.import' },
      { tab: 'assign', to: '/assign', labelKey: 'nav.assign' },
    ],
  },
  {
    key: 'reports', labelKey: 'nav.groupReports', icon: '📊',
    items: [
      { tab: 'admin_panel', to: '/admin', labelKey: 'nav.adminPanel' },
      { tab: 'search', to: '/admin/search', labelKey: 'nav.search' },
      { tab: 'checkpoints', to: '/admin/checkpointy', labelKey: 'nav.checkpoints' },
    ],
  },
  // Production analytics (spec: analityka-produkcji) — live view first, then the historical
  // screens, then the dictionaries the KPIs are computed from.
  {
    key: 'analytics', labelKey: 'nav.groupAnalytics', icon: '📈',
    items: [
      { tab: 'line_dashboard', to: '/admin/dashboard-linii', labelKey: 'nav.lineDashboard' },
      { tab: 'analytics_overview', to: '/analityka/przeglad', labelKey: 'nav.analyticsOverview' },
      { tab: 'analytics_lines', to: '/analityka/linie', labelKey: 'nav.analyticsLines' },
      { tab: 'analytics_line_history', to: '/analityka/historia-linii', labelKey: 'nav.analyticsLineHistory' },
      { tab: 'analytics_downtime', to: '/analityka/straty', labelKey: 'nav.analyticsDowntime' },
      { tab: 'analytics_production', to: '/analityka/produkcja', labelKey: 'nav.analyticsProduction' },
      { tab: 'analytics_shifts', to: '/analityka/zmiany', labelKey: 'nav.analyticsShifts' },
      { tab: 'analytics_orders', to: '/analityka/zlecenia', labelKey: 'nav.analyticsOrders' },
      { tab: 'analytics_capacity', to: '/analityka/capacity', labelKey: 'nav.analyticsCapacity' },
      { tab: 'analytics_ranking', to: '/analityka/ranking', labelKey: 'nav.analyticsRanking' },
      { tab: 'shift_output', to: '/admin/shift-output', labelKey: 'nav.shiftOutput' },
      { tab: 'winder', to: '/admin/nawijarka', labelKey: 'nav.winder' },
      { tab: 'analytics_standards', to: '/analityka/standardy', labelKey: 'nav.analyticsStandards' },
      { tab: 'materials', to: '/analityka/materialy', labelKey: 'nav.materials' },
      { tab: 'analytics_definitions', to: '/analityka/definicje', labelKey: 'nav.analyticsDefinitions' },
    ],
  },
  {
    key: 'settings', labelKey: 'nav.groupSettings', icon: '⚙️',
    items: [
      { tab: 'users', to: '/users', labelKey: 'nav.users' },
      { tab: 'spool_specs', to: '/admin/parametry-szpul', labelKey: 'nav.spoolSpecs' },
      { tab: 'appearance', to: '/admin/wyglad', labelKey: 'nav.appearance' },
      { tab: 'problem_types', to: '/admin/problem-types', labelKey: 'nav.problemTypes' },
      { tab: 'role_access', to: '/admin/dostep', labelKey: 'nav.roleAccess' },
    ],
  },
];

/** Path of the first menu tab the role may open — the start page of a role added by the admin. */
export const firstAllowedPath = (can: (tab: string) => boolean): string | null => {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.enabled !== false && can(item.tab)) {
        return typeof item.to === 'function' ? item.to(false) : item.to;
      }
    }
  }
  return null;
};

const Layout: React.FC = () => {
  const { user, logout, isManager, isPackaging, isCustomRole, activeItem, selectedShift, selectedLine, can } = useAuth();
  const { t } = useLanguage();
  // Mobile: links collapse behind a hamburger; closes itself after navigation.
  const [menuOpen, setMenuOpen] = useState(false);
  // Desktop: which dropdown group is open; closes on outside click and on navigation.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();
  const navLinksRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { setOpenGroup(null); setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!openGroup) return;
    const onDown = (e: MouseEvent) => {
      if (navLinksRef.current && !navLinksRef.current.contains(e.target as Node)) setOpenGroup(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openGroup]);

  const visibleGroups = NAV_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => (i.enabled ?? true) && can(i.tab)) }))
    .filter(g => g.items.length > 0);
  // Few tabs (line operators, packaging, tracker) fit in the bar as they always did —
  // dropdowns would only add a click. Grouping kicks in for manager/admin menus.
  const flatItems = visibleGroups.flatMap(g => g.items);
  const useGroupedMenu = flatItems.length > 6;
  const isActivePath = (to: string) => location.pathname === to || (to !== '/' && location.pathname.startsWith(to + '/'));
  // Prefix matching alone would light up two groups at once ("/admin" is a prefix of
  // "/admin/dashboard-linii"), so only the longest matching link counts as the current one.
  const resolveTo = (item: NavItem) => (typeof item.to === 'function' ? item.to(isPackaging) : item.to);
  const currentTo = flatItems.map(resolveTo).filter(isActivePath).sort((a, b) => b.length - a.length)[0] ?? null;
  const isCurrent = (to: string) => to === currentTo;
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showEndShiftModal, setShowEndShiftModal] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  const isAuthenticated = !!user;
  
  // Hold button state for 3 seconds
  const [holdProgress, setHoldProgress] = useState(0);
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const holdStartTimeRef = useRef<number | null>(null);
  const HOLD_DURATION = 3000; // 3 seconds

  const handleLogout = useCallback(() => {
    logout();
    // Always go to PIN login - line stays in context and cookie
    navigate('/login');
  }, [logout, navigate]);

  const handleEndShift = useCallback(() => {
    // Admin, manager, packaging station and roles added by the admin (no shift) can logout directly
    if (!isManager && !isPackaging && !isCustomRole) {
      setShowEndShiftModal(true);
    } else {
      handleLogout();
    }
  }, [isManager, isPackaging, isCustomRole, handleLogout]);

  const handleHoldStart = useCallback(() => {
    if (isManager || isPackaging || isCustomRole) {
      // Admins, managers, packaging station and roles added by the admin can logout directly without holding
      handleLogout();
      return;
    }

    holdStartTimeRef.current = Date.now();
    setHoldProgress(0);

    // Update progress every 50ms for smooth animation
    holdTimerRef.current = setInterval(() => {
      if (holdStartTimeRef.current) {
        const elapsed = Date.now() - holdStartTimeRef.current;
        const progress = Math.min((elapsed / HOLD_DURATION) * 100, 100);
        setHoldProgress(progress);

        if (elapsed >= HOLD_DURATION) {
          // Hold completed - trigger end shift
          if (holdTimerRef.current) {
            clearInterval(holdTimerRef.current);
            holdTimerRef.current = null;
          }
          holdStartTimeRef.current = null;
          setHoldProgress(0);
          handleEndShift();
        }
      }
    }, 50);
  }, [isManager, isPackaging, isCustomRole, handleEndShift, handleLogout]);

  const handleHoldEnd = useCallback(() => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    holdStartTimeRef.current = null;
    setHoldProgress(0);
  }, []);

  const handleEndShiftConfirm = useCallback(() => {
    setShowEndShiftModal(false);
    handleLogout();
  }, [handleLogout]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      handleEndShift();
    }, INACTIVITY_TIMEOUT);
  }, [handleEndShift]);

  // Wylogowanie na zmianie warty: 7:05 i 19:05 — kilka minut po starcie nowej zmiany,
  // żeby tablet nie został zalogowany na operatora, który już skończył pracę. Linia
  // pozostaje zalogowana (logout czyści tylko użytkownika), więc następna osoba
  // wchodzi swoim PIN-em. Dotyczy tylko roli "user" (operatorzy na liniach).
  //
  // Porównujemy czas logowania z ostatnią zmianą warty zamiast liczyć termin od chwili
  // montażu: termin liczony od "teraz" przepadał przy przeładowaniu strony (tablet
  // wybudzony po nocy, odświeżona karta) i przy każdym re-renderze po 7:05/19:05.
  const handleLogoutRef = useRef(handleLogout);
  handleLogoutRef.current = handleLogout;
  const isLineOperator = user?.role === 'user';

  useEffect(() => {
    if (!isLineOperator) {
      return;
    }

    const SHIFT_CHANGE_TIMES = [[7, 5], [19, 5]] as const;

    const lastShiftChange = (now: Date) => {
      let latest = -Infinity;
      [0, -1].forEach((dayOffset) => {
        SHIFT_CHANGE_TIMES.forEach(([hour, minute]) => {
          const candidate = new Date(now);
          candidate.setDate(candidate.getDate() + dayOffset);
          candidate.setHours(hour, minute, 0, 0);
          if (candidate.getTime() <= now.getTime()) {
            latest = Math.max(latest, candidate.getTime());
          }
        });
      });
      return latest;
    };

    const check = () => {
      const loggedInAt = Number(localStorage.getItem('loggedInAt'));
      if (!loggedInAt) {
        // Sesja sprzed wprowadzenia loggedInAt — liczymy od teraz, wyloguje na najbliższej zmianie.
        localStorage.setItem('loggedInAt', String(Date.now()));
        return;
      }
      if (loggedInAt < lastShiftChange(new Date())) {
        handleLogoutRef.current();
      }
    };

    check();
    const timer = setInterval(check, 30000);
    // Po wybudzeniu tabletu sprawdzamy od razu, nie czekając na najbliższy tik.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLineOperator]);

  const handleEndShiftCancel = useCallback(() => {
    // Don't allow canceling - user must select status
    // But we can allow it if they want to continue working
    // For now, we'll just close the modal and reset timer
    setShowEndShiftModal(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach((event) => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Start the timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
      }
    };
  }, [isAuthenticated, resetInactivityTimer]);

  return (
    <div>
      <nav
        className="nav"
        style={{
          background: '#667eea',
          padding: '12px 24px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          className="nav-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'center',
            gap: 24,
            maxWidth: 1600,
            margin: '0 auto',
            minHeight: 64,
          }}
        >
          <button
            type="button"
            className="nav-toggle"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(o => !o)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <Link
            to="/"
            className="nav-link nav-brand"
            style={{
              fontSize: 18,
              fontWeight: 700,
              margin: 0,
              padding: '8px 12px',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>🏭</span>
            <span>{t('nav.myProduction')}</span>
          </Link>

          <div
            ref={navLinksRef}
            className={`nav-links${menuOpen ? ' open' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 4,
              justifyContent: 'center',
            }}
          >
            {!useGroupedMenu && flatItems.map(item => {
              const to = typeof item.to === 'function' ? item.to(isPackaging) : item.to;
              return (
                <Link
                  key={item.tab}
                  to={to}
                  className="nav-link"
                  style={{ margin: 0 }}
                  onClick={() => setMenuOpen(false)}
                >
                  {t(item.labelKey)}
                </Link>
              );
            })}
            {useGroupedMenu && visibleGroups.map(group => {
              const groupActive = group.items.some(i => isCurrent(resolveTo(i)));
              const open = openGroup === group.key;
              return (
                <div key={group.key} className={`nav-group${open ? ' open' : ''}${groupActive ? ' active' : ''}`}>
                  <button
                    type="button"
                    className="nav-group-btn"
                    aria-expanded={open}
                    onClick={(e) => { e.stopPropagation(); setOpenGroup(open ? null : group.key); }}
                  >
                    <span className="nav-group-icon">{group.icon}</span>
                    <span>{t(group.labelKey)}</span>
                    <span className="nav-group-caret">▾</span>
                  </button>
                  <div className="nav-group-menu" role="menu">
                    {group.items.map(item => {
                      const to = typeof item.to === 'function' ? item.to(isPackaging) : item.to;
                      return (
                        <Link
                          key={item.tab}
                          to={to}
                          className={`nav-link nav-group-item${isCurrent(to) ? ' current' : ''}`}
                          style={{ margin: 0 }}
                          onClick={() => { setOpenGroup(null); setMenuOpen(false); }}
                        >
                          {t(item.labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="nav-user"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              paddingLeft: 16,
              borderLeft: '1px solid rgba(255,255,255,0.25)',
            }}
          >
            <LanguageSelector compact />
            <span
              className="nav-username"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 500,
                padding: '6px 10px',
                backgroundColor: 'rgba(255,255,255,0.12)',
                borderRadius: 6,
                whiteSpace: 'nowrap',
              }}
            >
              <span>👤</span>
              <span>{user?.name}</span>
            </span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                onMouseDown={handleHoldStart}
                onMouseUp={handleHoldEnd}
                onMouseLeave={handleHoldEnd}
                onTouchStart={handleHoldStart}
                onTouchEnd={handleHoldEnd}
                className="btn"
                style={{
                  padding: '8px 16px',
                  fontSize: 14,
                  position: 'relative',
                  overflow: 'hidden',
                  minWidth: 140,
                  backgroundColor: 'white',
                  color: '#667eea',
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                {(isManager || isPackaging) ? t('nav.logout') : (
                  holdProgress > 0 ? t('nav.holdProgress', { progress: Math.round(holdProgress).toString() }) : t('nav.endShift')
                )}
                {!isManager && !isPackaging && holdProgress > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      height: 4,
                      width: `${holdProgress}%`,
                      backgroundColor: '#28a745',
                      transition: 'width 0.05s linear',
                    }}
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="container">
        <AdminAppearanceProvider>
          <Outlet />
        </AdminAppearanceProvider>
      </main>

      {/* Blocking production checkpoint (every 2h + midnight) for anyone logged in on a line */}
      <CheckpointModal />
      {/* Winder detected a short spool — operator confirms whether it is waste */}
      <PartialSpoolModal />
      {/* The machine data shows the line has been standing for 30 min — on every screen */}
      <LineStoppageAlertModal />

      {showEndShiftModal && (
        <EndShiftModal
          activeItem={activeItem}
          selectedShift={selectedShift}
          selectedLineId={selectedLine?.id || null}
          onConfirm={handleEndShiftConfirm}
          onCancel={handleEndShiftCancel}
        />
      )}
    </div>
  );
};

export default Layout;

